import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/Approval.css";

// ══ FALLBACK LOOKUPS (used when live data doesn't resolve a label/icon) ══
const EXP_HEADS = {
  FUEL: { name: "Fuel / Diesel", ico: "⛽" },
  TOLL: { name: "Toll / Octroi", ico: "🛣️" },
  HAMALI: { name: "Hamali / Loading", ico: "💪" },
  CONVEY: { name: "Conveyance", ico: "🏍️" },
  PARK: { name: "Parking", ico: "🅿️" },
  FOOD: { name: "Food / Halt", ico: "🍽️" },
  DETENT: { name: "Detention", ico: "⏱️" },
  MISC: { name: "Miscellaneous", ico: "📋" },
};

const IMG_ICONS = {
  POD: "✍️",
  Package: "📦",
  Bill: "🧾",
  Label: "🏷️",
  Damage: "⚠️",
  Other: "📎",
};

const GRAD_FOR = (type) => {
  const m = {
    POD: "#dcfce7,#bbf7d0",
    Package: "#dbeafe,#bfdbfe",
    Bill: "#fef3c7,#fde68a",
    Label: "#ccfbf1,#99f6e4",
    Damage: "#fee2e2,#fecaca",
    Other: "#ede9fe,#ddd6fe",
  };
  return m[type] || "#f1f5f9,#e2e8f0";
};


/* ══ AUTOCOMPLETE ══
   Same search-as-you-type dropdown used for "Party" on
   ConsignmentBooking.jsx / Branch & Delivery Boy on Claims.jsx, ported
   here for the Delivery Boy / Destination filters. Styled to match the
   existing `.field input` / `.field select` look via the .apv-ac* rules
   in Approval.css. */
function AutoComplete({ options, value, onChange, placeholder = "Search…", disabled = false, inputId }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const [dropRect, setDropRect] = useState(null); // { top, left, width } in viewport coords
  const wrapRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else setQuery("");
  }, [value, selected]);

  const filtered = options.filter(
    (o) => o.value !== undefined && o.label != null &&
      String(o.label).toLowerCase().includes((query || "").toLowerCase())
  );

  useEffect(() => {
    if (focused >= 0 && optionRefs.current[focused]) {
      try {
        optionRefs.current[focused].scrollIntoView({ block: "nearest" });
      } catch (err) {
        // scrolling is a nice-to-have only
      }
    }
  }, [focused]);

  // Card containers use `overflow: hidden` (to clip the rounded header),
  // which would otherwise crop this dropdown after 2-3 rows even though
  // more options exist. Rendering it in a portal at a fixed viewport
  // position sidesteps that clipping entirely.
  useEffect(() => {
    if (!isOpen || !wrapRef.current) return;
    function updateRect() {
      const r = wrapRef.current.getBoundingClientRect();
      setDropRect({ top: r.bottom, left: r.left, width: r.width });
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen]);

  function hl(text, q) {
    if (!q) return text;
    const i = String(text).toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
  }

  function choose(opt) {
    onChange(String(opt.value));
    setQuery(opt.label);
    setIsOpen(false);
    setFocused(-1);
    setCursorMoved(false);
  }

  function clear() {
    onChange("");
    setQuery("");
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  function handleKeyDown(e) {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
        setCursorMoved(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.min(f + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (cursorMoved && filtered.length > 0) {
        const target = filtered[focused] || filtered[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
    }
  }

  return (
    <div className={`apv-ac${disabled ? " apv-ac--disabled" : ""}`} ref={wrapRef}>
      <div className={`apv-ac-box${isOpen ? " open" : ""}`}>
        <input
          id={inputId}
          className="apv-ac-in"
          type="text"
          autoComplete="off"
          placeholder={disabled ? "Loading…" : placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => { if (!disabled) { setIsOpen(true); setCursorMoved(false); } }}
          onClick={() => { if (!disabled && !isOpen) setIsOpen(true); }}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && !disabled && (
          <span className="apv-ac-x on" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
        )}
        {!disabled && (
          <span className="apv-ac-chev" onMouseDown={(e) => { e.preventDefault(); setIsOpen((o) => !o); }}>▾</span>
        )}
      </div>
      {isOpen && !disabled && dropRect && createPortal(
        <div
          className="apv-ac-drop open"
          style={{ position: "fixed", top: dropRect.top + 4, left: dropRect.left, width: dropRect.width }}
        >
          {filtered.length === 0 ? (
            <div className="apv-ac-empty">No matches found</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                ref={(el) => { optionRefs.current[i] = el; }}
                className={`apv-ac-opt${String(opt.value) === String(value) ? " sel" : ""}${i === focused ? " foc" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                onMouseEnter={() => setFocused(i)}
              >
                {hl(opt.label, query)}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// ══ UTILITIES ══
function fmt(n) {
  return (Math.round((n || 0) * 100) / 100)
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function fmtK(n) {
  return n >= 100000
    ? (n / 100000).toFixed(1) + "L"
    : n >= 1000
    ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "K"
    : n;
}

function fmtDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function claimTotal(c) {
  if (c.expenses && c.expenses.length) {
    return c.expenses.reduce((a, e) => a + e.amount, 0);
  }
  // Header-level rows (list view) don't carry expense lines — use the
  // claimed amount already computed server-side instead.
  return c.claimedAmountHeader || 0;
}

function initials(name) {
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

// ══ DROPDOWN FETCH HOOK ══
// Fetches options from the backend [HttpGet("dropdown")] endpoint for a given mode.
// Normalizes whatever column names come back (Emp_Code/Emp_Name, place_id/place_Name, ...)
// into a consistent { code, name } shape so the rest of the UI doesn't care about mode.
function useDropdown(mode, params = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await api.get(`/dropdown`, { params: { mode, ...params } });
        if (cancelled) return;
        const rows = res.data; // dropdown endpoint returns a bare array, not {success, data}
        const normalized = (rows || []).map((r) => {
          // mode=DEL  -> Emp_Code, Emp_Name
          // mode=plc  -> place_id, place_Name
          // mode=BRC  -> Branch_id, Branch_Name (aliased from place_id/place_Name)
          const code = r.Emp_Code ?? r.Branch_id ?? r.place_id ?? r.Place_id ?? "";
          const name = r.Emp_Name ?? r.Branch_Name ?? r.place_Name ?? r.Place_Name ?? "";
          return { code: String(code), name: String(name) };
        });
        setOptions(normalized);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || "Failed to load options");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [mode, JSON.stringify(params)]);

  return { options, loading, error };
}


// ══ LIVE DATA HOOKS (backend-fed) ══

// GET /get-all-claims — manager approval queue, all delivery boys.
// Returns header-level rows only (no consignments/expenses/images — see useClaimDetail).
function useAllClaims(filters = {}) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { deliveryBoyId, branchCode, fromDate, toDate } = filters;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await api.get(`/get-all-claims`, {
          params: { deliveryBoyId, branchCode, fromDate, toDate },
        });
        if (cancelled) return;
        const json = res.data;
        if (!json.success) throw new Error(json.message || "Failed to load claims");
        setClaims((json.data || []).map(mapHeaderClaim));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || "Failed to load claims");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [reloadKey, deliveryBoyId, branchCode, fromDate, toDate]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);
  return { claims, setClaims, loading, error, refetch };
}

// GET /get-claim-detail/{claimId} — full detail for the preview screen.
function useClaimDetail(claimId, expenseHeadMap) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(!!claimId);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!claimId) { setClaim(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await api.get(`/get-claim-detail/${claimId}`);
        if (cancelled) return;
        const json = res.data;
        if (!json.success) throw new Error(json.message || "Failed to load claim detail");
        setClaim(mapDetailClaim(json.data, expenseHeadMap));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || "Failed to load claim detail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [claimId, expenseHeadMap]);

  return { claim, loading, error };
}

// GET /get-expense-heads — id -> { name, icon } lookup, built once and reused.
function useExpenseHeadMap() {
  const [map, setMap] = useState(null); // null while loading, {} on empty/error
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/get-expense-heads`);
        if (cancelled) return;
        const json = res.data;
        const m = {};
        (json.data || []).forEach((h) => {
          m[h.expenseHeadId] = { name: h.headName, ico: h.iconEmoji || "💰" };
        });
        setMap(m);
      } catch {
        if (!cancelled) setMap({});
      }
    })();

    return () => { cancelled = true; };
  }, []);
  return map;
}

// ── Mappers: translate raw API shape into the field names the existing UI
// components already expect (claim.no, claim.boy, cnr, head, against, ...) ──

function mapHeaderClaim(c) {
  return {
    claimId: c.claimId,
    no: c.claimNo,
    boy: c.deliveryBoyId != null ? String(c.deliveryBoyId) : "",
    branch: c.branchCode || "",
    date: c.claimDate ? c.claimDate.slice(0, 10) : "",
    status: (c.status || "pending").toLowerCase(),
    approvedAmount: c.approvedAmount,
    claimedAmountHeader: c.claimedAmount,
    consignmentCount: c.consignmentCount,
    detailCount: c.detailCount,
    remark: c.claimRemark,
    // Detail (consignments/expenses/images) is loaded separately via useClaimDetail
    // when this claim is opened in preview, so these start empty.
    consignments: [],
    expenses: [],
    images: [],
  };
}

function mapDetailClaim(d, expenseHeadMap) {
  return {
    claimId: d.claimId,
    no: d.claimNo,
    boy: d.deliveryBoyId != null ? String(d.deliveryBoyId) : "",
    branch: d.branchCode || "",
    date: d.claimDate ? d.claimDate.slice(0, 10) : "",
    status: (d.status || "pending").toLowerCase(),
    approvedAmount: d.approvedAmount,
    paidAmount: d.paidAmount,
    approver: d.approverUser || undefined,
    approvedOn: d.decisionOn ? fmtDate(d.decisionOn) : undefined,
    approverRemark: d.approverRemark || undefined,
    rejectReason: d.rejectReason || undefined,
    paidOn: d.paidOn ? fmtDate(d.paidOn) : undefined,
    paymentRef: d.paymentRef || undefined,
    vehicleNo: d.vehicleNo,
    tripNo: d.tripNo,
    consignments: (d.consignments || []).map((c) => ({
      cnr: c.consignmentNo,
      from: c.fromPlace,
      to: c.toPlace,
      pcs: c.pcs,
      wt: c.weightKg,
      amount: c.freightAmount,
    })),
    expenses: (d.expenses || []).map((e) => {
      const head = (expenseHeadMap && expenseHeadMap[e.expenseHeadId]) || null;
      return {
        head: head ? head.name : `Head #${e.expenseHeadId}`,
        ico: head ? head.ico : "💰",
        amount: e.amount,
        against: e.againstConsignmentNo || "",
        remark: e.remark,
        hasBill: e.hasBill,
      };
    }),
    images: d.images || [], // already { type, cnr, name, url } from the backend
  };
}

// ══ TOAST ══
function ToastContainer({ toasts }) {
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.type}`}>
          <span style={{ fontSize: 15 }}>
            {t.type === "ok" ? "✓" : t.type === "err" ? "✕" : t.type === "warn" ? "⚠" : "ℹ"}
          </span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ══ LIGHTBOX ══
function Lightbox({ images, index, onClose, onNav }) {
  const img = images[index];
  if (!img) return null;
  return (
    <div className="lightbox show" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lb-content">
        <div className="lb-hd">
          <div>
            <h3 id="lb-title">{img.name}</h3>
            <p>{img.type} · {img.cnr || "Trip-level"}</p>
          </div>
          <button className="lb-x" onClick={onClose}>✕</button>
        </div>
        <div
          className="lb-img"
          style={!img.url ? { background: `linear-gradient(135deg,${GRAD_FOR(img.type)})` } : undefined}
        >
          {img.url ? (
            <img src={img.url} alt={img.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : (
            IMG_ICONS[img.type] || "📎"
          )}
        </div>
        <div className="lb-nav">
          <button className="lb-nav-btn" disabled={index === 0} onClick={() => onNav(-1)}>‹ Prev</button>
          <span className="lb-counter">{index + 1} / {images.length}</span>
          <button className="lb-nav-btn" disabled={index === images.length - 1} onClick={() => onNav(1)}>Next ›</button>
        </div>
      </div>
    </div>
  );
}

// ══ REJECT SHEET ══
function RejectSheet({ claimNo, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <div className="overlay show" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet">
        <div className="sheet-title">Reject Claim?</div>
        <div className="sheet-sub">
          Reject claim {claimNo}? The delivery boy will see your reason.
        </div>
        <div className="field" style={{ marginTop: 14 }}>
          <label>
            Rejection Reason <span style={{ color: "var(--red)" }}>*</span>
          </label>
          <textarea
            rows={3}
            placeholder="e.g. Toll receipt missing, amount exceeds policy…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="sheet-btns">
          <button className="btn btn-ghost btn-block" onClick={onClose}>Cancel</button>
          <button className="btn btn-red btn-block" onClick={() => onConfirm(reason)}>
            Confirm Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ══ IMAGE GALLERY ══
function ImageGallery({ claim, onOpenLightbox }) {
  const [imgFilter, setImgFilter] = useState("All");
  const types = ["All", ...new Set(claim.images.map((i) => i.type))];

  const pass = (img) => imgFilter === "All" || img.type === imgFilter;

  const flatDisplayed = [];
  const getIdx = (img) => {
    let i = flatDisplayed.findIndex((x) => x === img);
    if (i === -1) { i = flatDisplayed.length; flatDisplayed.push(img); }
    return i;
  };

  // Build flat list in display order
  const orderedImages = [];
  claim.consignments.forEach((cn) => {
    claim.images.filter((im) => im.cnr === cn.cnr && pass(im)).forEach((im) => orderedImages.push(im));
  });
  claim.images.filter((im) => (!im.cnr || im.cnr === "") && pass(im)).forEach((im) => orderedImages.push(im));

  return (
    <>
      <div className="img-filter">
        {types.map((t) => (
          <button
            key={t}
            className={`imgf-chip ${t === imgFilter ? "active" : ""}`}
            onClick={() => setImgFilter(t)}
          >
            {t}
            {t === "All"
              ? ` (${claim.images.length})`
              : ` (${claim.images.filter((i) => i.type === t).length})`}
          </button>
        ))}
      </div>

      {claim.consignments.map((cn, idx) => {
        const imgs = claim.images.filter((im) => im.cnr === cn.cnr && pass(im));
        return (
          <div className="img-cnr-group" key={cn.cnr}>
            <div className="img-cnr-head">
              <span className="img-cnr-sn">{idx + 1}</span>
              <div>
                <div className="img-cnr-no">{cn.cnr}</div>
                <div className="img-cnr-route">{cn.from} → {cn.to}</div>
              </div>
              <span className="img-cnr-count">
                {imgs.length} image{imgs.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="img-cnr-body">
              {imgs.length ? (
                <div className="img-gallery">
                  {imgs.map((im, i) => {
                    const flatIdx = orderedImages.indexOf(im);
                    return (
                      <GalItem key={i} img={im} onClick={() => onOpenLightbox(orderedImages, flatIdx)} />
                    );
                  })}
                </div>
              ) : (
                <div className="img-cnr-empty">
                  No {imgFilter === "All" ? "" : imgFilter + " "}images for this consignment
                </div>
              )}
            </div>
          </div>
        );
      })}

      {(() => {
        const general = claim.images.filter((im) => (!im.cnr || im.cnr === "") && pass(im));
        if (!general.length) return null;
        return (
          <div className="img-cnr-group general">
            <div className="img-cnr-head">
              <span className="img-cnr-sn">★</span>
              <div>
                <div className="img-cnr-no">General / Trip-level</div>
                <div className="img-cnr-route">Not tied to a specific consignment</div>
              </div>
              <span className="img-cnr-count">
                {general.length} image{general.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="img-cnr-body">
              <div className="img-gallery">
                {general.map((im, i) => {
                  const flatIdx = orderedImages.indexOf(im);
                  return (
                    <GalItem key={i} img={im} onClick={() => onOpenLightbox(orderedImages, flatIdx)} />
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

function GalItem({ img, onClick }) {
  return (
    <div className="gal-item" onClick={onClick}>
      <div
        className="gal-thumb"
        style={!img.url ? { background: `linear-gradient(135deg,${GRAD_FOR(img.type)})` } : undefined}
      >
        {img.url ? (
          <img src={img.url} alt={img.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          IMG_ICONS[img.type] || "📎"
        )}
      </div>
      <span className={`gal-type-tag gtt-${img.type.toLowerCase()}`}>{img.type}</span>
      <div className="gal-view">
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </div>
      <div className="gal-cap">{img.name}</div>
    </div>
  );
}

// ══ APPROVAL SIDEBAR ══
function ApprovalSidebar({ claim, onApprove, onReject }) {
  const total = claimTotal(claim);
  const [apprAmount, setApprAmount] = useState(total);
  const [remark, setRemark] = useState("");

  useEffect(() => {
    setApprAmount(total);
    setRemark("");
  }, [claim.no, total]);

  const variance = (() => {
    if (apprAmount === total) return { cls: "variance show same", msg: "✓ Approving full claimed amount" };
    if (apprAmount < total) return { cls: "variance show cut", msg: `▼ ₹${fmt(total - apprAmount)} less than claimed (₹${fmt(total)})` };
    return { cls: "variance show same", msg: `▲ ₹${fmt(apprAmount - total)} more than claimed` };
  })();

  if (claim.status !== "pending") {
    return (
      <div className="card appr-card">
        <div className="band green-band">
          <div className="band-icon">
            <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div>
            <div className="band-title">Approval Decision</div>
            <div className="band-sub">Decision recorded</div>
          </div>
        </div>
        <div className="card-body">
          {claim.status === "approved" ? (
            <>
              <div style={{ textAlign: "center", padding: "6px 0 14px" }}>
                <div style={{ fontSize: 34 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "var(--green)", marginTop: 4 }}>Approved</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "var(--navy)", marginTop: 8 }}>
                  ₹ {fmt(claim.approvedAmount)}
                </div>
                {claim.approvedAmount < total && (
                  <div style={{ fontSize: 11.5, color: "var(--red)", fontWeight: 700, marginTop: 3 }}>
                    ₹{fmt(total - claim.approvedAmount)} reduced from claim
                  </div>
                )}
              </div>
              <div className="appr-flow">
                <ApprStep done label={`Submitted by ${claim.boy}`} meta={fmtDate(claim.date)} last={false} />
                <ApprStep done label={`Approved by ${claim.approver}`} meta={claim.approvedOn} last />
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: "center", padding: "6px 0 14px" }}>
                <div style={{ fontSize: 34 }}>🚫</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "var(--red)", marginTop: 4 }}>Rejected</div>
              </div>
              <div style={{ background: "var(--red-lt)", borderRadius: 8, padding: "11px 13px", fontSize: 12.5, color: "#991b1b", lineHeight: 1.5 }}>
                <b>Reason:</b> {claim.rejectReason || "—"}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 10, textAlign: "center" }}>
                by {claim.approver} · {claim.approvedOn}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card appr-card">
      <div className="band green-band">
        <div className="band-icon">
          <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <div>
          <div className="band-title">Approval Decision</div>
          <div className="band-sub">Approve any amount</div>
        </div>
      </div>
      <div className="card-body">
        <div className="appr-amount-box">
          <div className="appr-amount-lbl">Approved Amount</div>
          <div className="appr-amt-input-wrap">
            <span className="appr-amt-prefix">₹</span>
            <input
              className="appr-amt-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="1"
              value={apprAmount}
              onChange={(e) => setApprAmount(parseFloat(e.target.value) || 0)}
            />
          </div>
          <div className="appr-claimed-note">
            <span>Claimed: <b>₹ {fmt(total)}</b></span>
            <button className="appr-quick-btn" onClick={() => setApprAmount(total)}>Approve Full</button>
          </div>
          <div className={variance.cls}>{variance.msg}</div>
        </div>

        <div className="field" style={{ marginBottom: 12 }}>
          <label>
            Approver Remark{" "}
            <span style={{ color: "var(--muted)", fontWeight: 600, textTransform: "none" }}>(optional)</span>
          </label>
          <textarea
            rows={2}
            placeholder="Add a note for this decision…"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <button className="btn btn-green btn-lg btn-block" onClick={() => onApprove(apprAmount, remark)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Approve Claim
          </button>
          <button className="btn btn-red btn-block" onClick={onReject}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Reject Claim
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprStep({ done, label, meta, last }) {
  return (
    <div className={`appr-step${done ? " done" : ""}`}>
      <div className="appr-dot-wrap" style={!last ? undefined : { paddingBottom: 0 }}>
        {!last && (
          <div style={{
            position: "absolute", left: 13, top: 28, bottom: -6,
            width: 2, background: done ? "var(--green)" : "var(--border)"
          }} />
        )}
        <div className={`appr-dot${done ? " done" : ""}`}>
          <svg viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      </div>
      <div className="appr-body-step" style={last ? { paddingBottom: 0 } : undefined}>
        <div className="appr-title">{label}</div>
        <div className="appr-meta">{meta}</div>
      </div>
    </div>
  );
}

// ══ PREVIEW VIEW ══
function PreviewView({ claim, onBack, onApprove, onReject }) {
  const [lbImages, setLbImages] = useState([]);
  const [lbIndex, setLbIndex] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const total = claimTotal(claim);
  const statusLabels = { pending: "⏳ Pending", approved: "✓ Approved", rejected: "✕ Rejected" };

  const handleOpenLightbox = (images, idx) => {
    setLbImages(images);
    setLbIndex(idx);
    setLbOpen(true);
  };

  const handleApprove = (amount, remark) => {
    if (isNaN(amount) || amount < 0) { return; }
    onApprove(claim.no, amount, remark);
  };

  const handleConfirmReject = (reason) => {
    if (!reason) return;
    setShowReject(false);
    onReject(claim.no, reason);
  };

  return (
    <div>
      <div className="pg-row">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back to Queue
        </button>
      </div>

      {/* Banner */}
      <div className="pv-banner">
        <div className="pvb-top">
          <div>
            <div className="pvb-label">Expense Claim</div>
            <div className="pvb-no">{claim.no}</div>
          </div>
          <div className="pvb-claimed">
            <div className="pvb-claimed-val">₹ {fmt(total)}</div>
            <div className="pvb-claimed-lbl">Claimed Amount</div>
          </div>
        </div>
        <div className="pvb-meta">
          <div className="pvb-meta-item">
            <div className="pvb-meta-lbl">Delivery Boy</div>
            <div className="pvb-meta-val">{claim.boy}</div>
          </div>
          <div className="pvb-meta-item">
            <div className="pvb-meta-lbl">Date</div>
            <div className="pvb-meta-val">{fmtDate(claim.date)}</div>
          </div>
          <div className="pvb-meta-item">
            <div className="pvb-meta-lbl">Branch</div>
            <div className="pvb-meta-val">{claim.branch}</div>
          </div>
          <div className="pvb-meta-item">
            <div className="pvb-meta-lbl">Status</div>
            <div className="pvb-meta-val">{statusLabels[claim.status] || claim.status}</div>
          </div>
        </div>
      </div>

      <div className="pv-grid">
        {/* LEFT */}
        <div>
          {/* Consignments */}
          <div className="card">
            <div className="band teal-band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                </svg>
              </div>
              <div>
                <div className="band-title">Consignments</div>
                <div className="band-sub">Covered in this claim</div>
              </div>
              <div className="band-r">
                <span className="band-badge">{claim.consignments.length} CNR</span>
              </div>
            </div>
            <div className="cnt-grid-scroll">
              <table className="cnt-grid">
                <thead>
                  <tr>
                    <th className="c">#</th>
                    <th>Consignment No.</th>
                    <th className="c">Pcs</th>
                    <th className="r">Weight</th>
                    <th className="r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {claim.consignments.map((x, i) => (
                    <tr key={x.cnr}>
                      <td className="c"><span className="cg-sn">{i + 1}</span></td>
                      <td>
                        <div className="cg-cnr">{x.cnr}</div>
                        <div className="cg-route">{x.from} → {x.to}</div>
                      </td>
                      <td className="c"><span className="cg-pcs">{x.pcs}</span></td>
                      <td className="r"><span className="cg-wt">{x.wt} kg</span></td>
                      <td className="r"><span className="cg-amt">₹ {fmt(x.amount)}</span></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="lbl">Total</td>
                    <td className="c">{claim.consignments.reduce((a, x) => a + x.pcs, 0)}</td>
                    <td className="r">{fmt(claim.consignments.reduce((a, x) => a + x.wt, 0))} kg</td>
                    <td className="r">₹ {fmt(claim.consignments.reduce((a, x) => a + x.amount, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Expense Heads */}
          <div className="card">
            <div className="band amber-band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              </div>
              <div>
                <div className="band-title">Expense Heads</div>
                <div className="band-sub">Claimed amounts per head</div>
              </div>
            </div>
            <div className="card-body">
              {claim.expenses.map((e, i) => {
                const h = EXP_HEADS[e.head] || { name: e.head, ico: e.ico || "💰" };
                return (
                  <div className="exp-row" key={i}>
                    <div className="exp-l">
                      <div className="exp-ico">{h.ico}</div>
                      <div>
                        <div className="exp-name">{h.name}</div>
                        <div className="exp-against">
                          {e.against ? `Against: ${e.against}` : "All consignments"}
                        </div>
                      </div>
                    </div>
                    <div className="exp-amt">₹ {fmt(e.amount)}</div>
                  </div>
                );
              })}
              <div className="exp-total-row">
                <span className="exp-total-lbl">Total Claimed</span>
                <span className="exp-total-val">₹ {fmt(total)}</span>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="card">
            <div className="band navy-band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              <div>
                <div className="band-title">POD &amp; Uploaded Images</div>
                <div className="band-sub">Grouped by consignment · tap to view</div>
              </div>
              <div className="band-r">
                <span className="band-badge">
                  {claim.images.length} image{claim.images.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="card-body">
              <ImageGallery claim={claim} onOpenLightbox={handleOpenLightbox} />
            </div>
          </div>
        </div>

        {/* RIGHT: Approval Sidebar */}
        <div>
          <ApprovalSidebar
            claim={claim}
            onApprove={handleApprove}
            onReject={() => setShowReject(true)}
          />
        </div>
      </div>

      {lbOpen && (
        <Lightbox
          images={lbImages}
          index={lbIndex}
          onClose={() => setLbOpen(false)}
          onNav={(d) => setLbIndex((i) => Math.max(0, Math.min(lbImages.length - 1, i + d)))}
        />
      )}

      {showReject && (
        <RejectSheet
          claimNo={claim.no}
          onClose={() => setShowReject(false)}
          onConfirm={handleConfirmReject}
        />
      )}
    </div>
  );
}

// ══ CLAIM CARD ══
function ClaimCard({ claim, onPreview, onQuickApprove, boyDisplay }) {
  const total = claimTotal(claim);
  const dests = [...new Set(claim.consignments.map((x) => x.to))];
  const showAmt = claim.status === "approved" ? claim.approvedAmount : total;
  const labels = { pending: "⏳ Pending", approved: "✓ Approved", rejected: "✕ Rejected" };
  const boyName = boyDisplay ? boyDisplay(claim.boy) : claim.boy;

  // List rows only carry header-level counts (consignmentCount/detailCount);
  // full consignments/expenses/images arrays load on preview via get-claim-detail.
  const consignmentCount = claim.consignments.length || claim.consignmentCount || 0;
  const expenseCount = claim.expenses.length || claim.detailCount || 0;

  return (
    <div className="claim-card">
      <div className="cc-head" onClick={() => onPreview(claim.no)}>
        <div className="cc-boy-av">{initials(boyName || "?")}</div>
        <div className="cc-main">
          <div className="cc-top">
            <span className="cc-no">{claim.no}</span>
          </div>
          <div className="cc-meta">
            <span className="cc-boy">🛵 {boyName}</span>
            <span className="cc-meta-dot" />
            <span>{fmtDate(claim.date)}</span>
            <span className="cc-meta-dot" />
            <span>{claim.branch}</span>
          </div>
        </div>
        <div className="cc-amt-wrap">
          <div className="cc-amt">₹ {fmt(showAmt)}</div>
          <div className="cc-amt-lbl">{claim.status === "approved" ? "Approved" : "Claimed"}</div>
        </div>
      </div>
      <div className="cc-tags">
        {dests.length > 0 && <span className="cc-tag dest">📍 {dests.join(", ")}</span>}
        <span className="cc-tag cnt">
          📦 {consignmentCount} consignment{consignmentCount !== 1 ? "s" : ""}
        </span>
        <span className="cc-tag">
          {expenseCount} expense head{expenseCount !== 1 ? "s" : ""}
        </span>
        {claim.images.length > 0 && (
          <span className="cc-tag img">
            🖼️ {claim.images.length} image{claim.images.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="cc-foot">
        <span className={`pill pill-${claim.status}`}>{labels[claim.status]}</span>
        {claim.status === "rejected" && (
          <span style={{ fontSize: 11, color: "var(--red)" }}>{claim.rejectReason || ""}</span>
        )}
        {claim.status === "approved" && (
          <span style={{ fontSize: 11, color: "var(--muted)" }}>
            by {claim.approver} · {claim.approvedOn}
          </span>
        )}
        <div className="cc-foot-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => onPreview(claim.no)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Preview
          </button>
          {claim.status === "pending" && (
            <button className="btn btn-green btn-sm" onClick={() => onQuickApprove(claim.no)}>
              Approve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══ LIST VIEW ══
function ListView({
  claims, setClaims, onPreview, addToast, boyDisplay,
  filterBoy, setFilterBoy, filterDest, setFilterDest,
  filterFrom, setFilterFrom, filterTo, setFilterTo,
}) {
  const [curStatus, setCurStatus] = useState("pending");

  // Delivery Boy + Destination options come from the backend dropdown endpoint.
  // Destination doubles as "Branch" — same place_id/code system as
  // ExpenseClaim.BranchCode — so there's no separate Branch filter.
  const { options: boys, loading: boysLoading, error: boysError } = useDropdown("DEL");
  const { options: dests, loading: destsLoading, error: destsError } = useDropdown("plc");

  // Delivery Boy / Destination(Branch) / Date are all filtered server-side now
  // (see useAllClaims in the root component) — `claims` arriving here is
  // already scoped to them. Only status remains client-side, since it's a UI
  // tab over already-loaded data rather than a server query param.
  const filtered = claims.filter((c) => {
    if (curStatus !== "all" && c.status !== curStatus) return false;
    return true;
  });

  const pending = claims.filter((c) => c.status === "pending");
  const approved = claims.filter((c) => c.status === "approved");

  const handleQuickApprove = async (no) => {
    const target = claims.find((c) => c.no === no);
    if (!target) return;
    const amount = claimTotal(target);

    try {
      const res = await api.post(`/decide-claim/${target.claimId}`, {
        status: "Approved",
        approvedAmount: amount,
      });
      const json = res.data;
      if (!json.success) throw new Error(json.message || "Approve failed");

      setClaims((prev) =>
        prev.map((c) => {
          if (c.no !== no) return c;
          return {
            ...c,
            status: "approved",
            approvedAmount: json.data?.approvedAmount ?? amount,
            approver: json.data?.approverUser,
            approvedOn: json.data?.decisionOn ? fmtDate(json.data.decisionOn) : undefined,
          };
        })
      );
      addToast(`${no} approved for full ₹${fmt(amount)}`, "ok");
    } catch (err) {
      addToast(err.response?.data?.message || err.message || `Failed to approve ${no}`, "err");
    }
  };

  const chipClass = (s) => {
    let cls = "schip";
    if (curStatus === s) {
      cls += " active";
      if (s === "approved") cls += " s-approved";
      if (s === "rejected") cls += " s-rejected";
      if (s === "all") cls += " s-all";
    }
    return cls;
  };

  return (
    <div>
      <div className="pg-row">
        <div className="pg-title">
          <h1>Expense Claim Approval</h1>
          <p>Review delivery-boy expense claims and approve or reject</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-row">
        <div className="stat-box amber">
          <div className="sb-val">{pending.length}</div>
          <div className="sb-lbl">Pending Claims</div>
        </div>
        <div className="stat-box navy">
          <div className="sb-val">₹{fmtK(pending.reduce((a, c) => a + claimTotal(c), 0))}</div>
          <div className="sb-lbl">Pending Amount</div>
        </div>
        <div className="stat-box green">
          <div className="sb-val">₹{fmtK(approved.reduce((a, c) => a + (c.approvedAmount || 0), 0))}</div>
          <div className="sb-lbl">Approved (Month)</div>
        </div>
        <div className="stat-box blue">
          <div className="sb-val">{new Set(pending.map((c) => c.boy)).size}</div>
          <div className="sb-lbl">Delivery Boys</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="band navy-band">
          <div className="band-icon">
            <svg viewBox="0 0 24 24" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
          </div>
          <div>
            <div className="band-title">Filters</div>
            <div className="band-sub">Narrow the approval queue</div>
          </div>
        </div>
        <div className="card-body">
          <div className="filter-grid">
            <div className="field">
              <label>Delivery Boy</label>
              <AutoComplete
                options={boys.map((b) => ({ value: b.code, label: b.name }))}
                value={filterBoy}
                onChange={setFilterBoy}
                placeholder={boysError ? "Failed to load" : "All Delivery Boys"}
                disabled={boysLoading}
                inputId="apv-filter-boy-input"
              />
              {boysError && <div className="field-err">{boysError}</div>}
            </div>
            <div className="field">
              <label>Destination / Branch</label>
              <AutoComplete
                options={dests.map((d) => ({ value: d.code, label: d.name }))}
                value={filterDest}
                onChange={setFilterDest}
                placeholder={destsError ? "Failed to load" : "All Destinations"}
                disabled={destsLoading}
                inputId="apv-filter-dest-input"
              />
              {destsError && <div className="field-err">{destsError}</div>}
            </div>
            <div className="field">
              <label>From Date</label>
              <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
            </div>
            <div className="field">
              <label>To Date</label>
              <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
              {(filterFrom || filterTo) && !(filterFrom && filterTo) && (
                <div className="field-err">Set both From and To to filter by date</div>
              )}
            </div>
          </div>
          <div className="filter-actions">
            <div className="status-chips">
              <button className={chipClass("pending")} onClick={() => setCurStatus("pending")}>⏳ Pending</button>
              <button className={chipClass("approved")} onClick={() => setCurStatus("approved")}>✓ Approved</button>
              <button className={chipClass("rejected")} onClick={() => setCurStatus("rejected")}>✕ Rejected</button>
              <button className={chipClass("all")} onClick={() => setCurStatus("all")}>All</button>
            </div>
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: "auto" }}
              onClick={() => { setFilterBoy(""); setFilterDest(""); setFilterFrom(""); setFilterTo(""); }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Claim List */}
      <div className="claim-list">
        {filtered.length === 0 ? (
          <div className="card">
            <div className="empty">
              <div className="empty-ico">✅</div>
              <div className="empty-title">No claims found</div>
              <div className="empty-sub">
                No {curStatus === "all" ? "" : curStatus + " "}claims match the current filters
              </div>
            </div>
          </div>
        ) : (
          filtered.map((c) => (
            <ClaimCard key={c.no} claim={c} onPreview={onPreview} onQuickApprove={handleQuickApprove} boyDisplay={boyDisplay} />
          ))
        )}
      </div>
    </div>
  );
}

// ══ ROOT APP ══
export default function Approval() {
  // Server-side filters for the approval queue — lifted here since
  // useAllClaims needs them to build the query params.
  const [filterBoy, setFilterBoy] = useState("");
  // filterDest doubles as the Branch filter — Destination and Branch share
  // the same place_id/code system, so there's no separate Branch control.
  const [filterDest, setFilterDest] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // fromDate/toDate only apply together — matches the backend's "both or neither" rule.
  const dateFiltersActive = !!(filterFrom && filterTo);

  const { claims: rawClaims, setClaims, loading: claimsLoading, error: claimsError, refetch } = useAllClaims({
    deliveryBoyId: filterBoy || undefined,
    branchCode: filterDest || undefined,
    fromDate: dateFiltersActive ? filterFrom : undefined,
    toDate: dateFiltersActive ? filterTo : undefined,
  });
  const expenseHeadMap = useExpenseHeadMap();
  const { options: boyOptions } = useDropdown("DEL"); // Emp_Code -> Emp_Name, for display only
  const [view, setView] = useState("list"); // "list" | "preview"
  const [previewNo, setPreviewNo] = useState(null);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((msg, type = "info") => {
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => {
      setToasts((t) => t.map((x) => x.id === id ? { ...x, fading: true } : x));
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 320);
    }, 2800);
  }, []);

  // claim.boy carries the raw DeliveryBoyId/Emp_Code for filtering correctness;
  // boyDisplay resolves that code to a name purely for what's shown on screen.
  const boyNameByCode = {};
  boyOptions.forEach((b) => { boyNameByCode[b.code] = b.name; });
  const boyDisplay = (boy) => boyNameByCode[boy] || boy || "Unknown";

  const claims = rawClaims; // filtering/matching stays on raw codes; display resolves separately


  const breadcrumb = view === "preview" ? "Claim Preview" : "Approval Queue";
  const headerClaim = claims.find((c) => c.no === previewNo);

  // Header row (from the list) only has summary fields — full consignments/
  // expenses/images for the preview come from /get-claim-detail/{claimId}.
  const { claim: detailClaimRaw, loading: detailLoading, error: detailError } =
    useClaimDetail(headerClaim?.claimId, expenseHeadMap);

  const detailClaim = detailClaimRaw
    ? { ...detailClaimRaw, boy: boyDisplay(detailClaimRaw.boy) }
    : null;

  const openPreview = (no) => { setPreviewNo(no); setView("preview"); window.scrollTo(0, 0); };
  const showList = () => { setView("list"); setPreviewNo(null); window.scrollTo(0, 0); };

  // Approve/Reject now call the real backend endpoint (POST /decide-claim/{claimId}).
  // Local state updates optimistically after a successful response; on failure
  // the list is left untouched and an error toast is shown instead.
  const handleApprove = async (no, amount, remark) => {
    const target = claims.find((c) => c.no === no);
    if (!target) return;

    try {
      const res = await api.post(`/decide-claim/${target.claimId}`, {
        status: "Approved",
        approvedAmount: amount,
        approverRemark: remark,
      });
      const json = res.data;
      if (!json.success) throw new Error(json.message || "Approve failed");

      setClaims((prev) =>
        prev.map((c) =>
          c.no !== no ? c : {
            ...c,
            status: "approved",
            approvedAmount: json.data?.approvedAmount ?? amount,
            approver: json.data?.approverUser,
            approvedOn: json.data?.decisionOn ? fmtDate(json.data.decisionOn) : undefined,
            approverRemark: remark,
          }
        )
      );
      addToast(`Claim ${no} approved for ₹${fmt(amount)}`, "ok");
      setTimeout(showList, 700);
    } catch (err) {
      addToast(err.response?.data?.message || err.message || `Failed to approve ${no}`, "err");
    }
  };

  const handleReject = async (no, reason) => {
    const target = claims.find((c) => c.no === no);
    if (!target) return;

    try {
      const res = await api.post(`/decide-claim/${target.claimId}`, {
        status: "Rejected",
        rejectReason: reason,
      });
      const json = res.data;
      if (!json.success) throw new Error(json.message || "Reject failed");

      setClaims((prev) =>
        prev.map((c) =>
          c.no !== no ? c : {
            ...c,
            status: "rejected",
            rejectReason: reason,
            approver: json.data?.approverUser,
            approvedOn: json.data?.decisionOn ? fmtDate(json.data.decisionOn) : undefined,
          }
        )
      );
      addToast(`Claim ${no} rejected`, "info");
      setTimeout(showList, 600);
    } catch (err) {
      addToast(err.response?.data?.message || err.message || `Failed to reject ${no}`, "err");
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && view === "preview") showList();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [view]);

  return (
    <>
      {/* Header */}
      <header className="hdr">
        <div className="logo">
          <div className="logo-mark">
            <svg viewBox="0 0 24 24">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
            </svg>
          </div>
          <div>
            <div className="logo-name">Saami <span>Tradestar</span></div>
            <div className="logo-pvt">Expense Approval</div>
          </div>
        </div>
        <div className="hdr-r">
          <span className="hdr-pill">{breadcrumb}</span>
          <div className="hdr-user">
            <div className="hdr-av">MG</div>
            Manager
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bc">
        <span>Finance</span>
        <span className="bc-sep">›</span>
        <span>Expense Claims</span>
        <span className="bc-sep">›</span>
        <span className="bc-cur">{breadcrumb}</span>
      </div>

      {/* Page */}
      <div className="page">
        {view === "list" ? (
          claimsLoading ? (
            <div className="card"><div className="empty"><div className="empty-title">Loading claims…</div></div></div>
          ) : claimsError ? (
            <div className="card">
              <div className="empty">
                <div className="empty-ico">⚠️</div>
                <div className="empty-title">Couldn't load claims</div>
                <div className="empty-sub">{claimsError}</div>
                <button className="btn btn-ghost btn-sm" onClick={refetch} style={{ marginTop: 12 }}>Retry</button>
              </div>
            </div>
          ) : (
            <ListView
              claims={claims}
              setClaims={setClaims}
              onPreview={openPreview}
              addToast={addToast}
              boyDisplay={boyDisplay}
              filterBoy={filterBoy}
              setFilterBoy={setFilterBoy}
              filterDest={filterDest}
              setFilterDest={setFilterDest}
              filterFrom={filterFrom}
              setFilterFrom={setFilterFrom}
              filterTo={filterTo}
              setFilterTo={setFilterTo}
            />
          )
        ) : detailLoading ? (
          <div className="card"><div className="empty"><div className="empty-title">Loading claim detail…</div></div></div>
        ) : detailError ? (
          <div className="card">
            <div className="empty">
              <div className="empty-ico">⚠️</div>
              <div className="empty-title">Couldn't load claim detail</div>
              <div className="empty-sub">{detailError}</div>
              <button className="btn btn-ghost btn-sm" onClick={showList} style={{ marginTop: 12 }}>Back to list</button>
            </div>
          </div>
        ) : detailClaim ? (
          <PreviewView
            claim={detailClaim}
            onBack={showList}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ) : null}
      </div>

      <ToastContainer toasts={toasts} />
    </>
  );
}
